/**
 * LightboxModal - Fullscreen modal for viewing enlarged gallery images.
 *
 * Features:
 * - Dark backdrop with click-to-close
 * - Large centered image display
 * - Navigation arrows and keyboard support (left/right arrows, ESC to close)
 * - Image counter display
 * - Optional filmstrip thumbnail navigation
 *
 * @see GalleryDetailContext - Provides lightbox state management
 * @see LightboxFilmstrip - Optional thumbnail navigation component
 */

import { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { GalleryAllImages } from '../../types'

interface LightboxModalProps {
  images: GalleryAllImages[]
  currentIndex: number
  onClose: () => void
  onNavigate: (direction: 'prev' | 'next') => void
  onIndexChange?: (index: number) => void
}

export function LightboxModal({
  images,
  currentIndex,
  onClose,
  onNavigate,
  onIndexChange,
}: LightboxModalProps) {
  const currentImage = images[currentIndex]

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          onNavigate('prev')
          break
        case 'ArrowRight':
          onNavigate('next')
          break
      }
    },
    [onClose, onNavigate]
  )

  // Set up keyboard listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!currentImage) return null

  const modalContent = (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 text-white hover:bg-white/20 h-10 w-10"
        onClick={onClose}
        aria-label="Close lightbox"
      >
        <X className="h-6 w-6" />
      </Button>

      {/* Navigation - Previous */}
      {images.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 h-12 w-12"
          onClick={() => onNavigate('prev')}
          aria-label="Previous image"
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>
      )}

      {/* Navigation - Next */}
      {images.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 h-12 w-12"
          onClick={() => onNavigate('next')}
          aria-label="Next image"
        >
          <ChevronRight className="h-8 w-8" />
        </Button>
      )}

      {/* Main image container */}
      <div className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={currentImage.image_url || ''}
          alt={currentImage.caption ?? `Image ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          draggable={false}
        />
      </div>

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
          <Badge className="bg-black/60 text-white text-sm px-3 py-1.5 backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </Badge>
        </div>
      )}

      {/* Caption (if available and not a system filename) */}
      {currentImage.caption &&
       !currentImage.caption.includes('rn_image_picker') &&
       !currentImage.caption.includes('temp_') &&
       !currentImage.caption.match(/^[a-f0-9-]{20,}/) && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-lg text-center">
          <p className="text-white/90 text-sm bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm">
            {currentImage.caption}
          </p>
        </div>
      )}

      {/* Thumbnail navigation dots for small sets */}
      {images.length > 1 && images.length <= 10 && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              className={cn(
                'w-2 h-2 rounded-full transition-all',
                index === currentIndex
                  ? 'bg-white scale-125'
                  : 'bg-white/40 hover:bg-white/60'
              )}
              onClick={() => onIndexChange?.(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )

  // Use portal to render at document body level
  return createPortal(modalContent, document.body)
}

export default LightboxModal
